
import { AsyncPipe } from "@angular/common";
import {
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ChangeDetectionStrategy
} from "@angular/core";
import { BlockComponent } from "@components/container-block/block.component";
import { VerticalCollapsableComponent } from "@components/vertical-collapsable/vertical-collapsable.component";
import moment from "moment";
import { Subject } from "rxjs";
import { filter, map, takeUntil, withLatestFrom } from "rxjs/operators";
import { TabState } from "~app/common/components/tabs/tab/itab";
import { VirkeskopKontraktstatus } from "~app/common/models/constants";
import { IVirkeskopKontraktEntityDTO } from "~app/common/models/entity-models";
import { RuleErrorContext, RuleSeverityLevel, RuleValidationMessage, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Kontrakt } from "~app/features/virkeskop/models/kontrakt-models";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { VkKartaComponent } from "../../karta/vk-karta/vk-karta.component";
import { IAtgardNettoareal } from "../../models/avverkningsanmalan-models";
import { Atgardstyper } from "../../models/virkeskopAtgardstyper";
import { AllSkogsvardTradslagService } from "../../services/all-skogsvard-tradslag.service";
import { AtgardKedjaMediatorService } from "../../services/atgardkedja-mediator.service";
import { KontraktCommandService } from "../../services/kontrakt-command.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { VirkeskopskontraktLeverantorMediatorService } from "../../services/virkeskopskontrakt-leverantor-mediator.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";
import { SkogsvardFpvKontraktComponent } from "../skogsvard-fpv-kontrakt/skogsvard-fpv-kontrakt.component";
import { SkogsvardKontraktComponent } from "../skogsvard-kontrakt/skogsvard-kontrakt.component";
import { VirkeskopKontraktComponent } from "../virkeskop-kontrakt/virkeskop-kontrakt.component";

@Component({
    selector: "rask-kontrakt-item",
    templateUrl: "./kontrakt-item.component.html",
    styleUrls: ["./kontrakt-item.component.scss"],
    providers: [
        KontraktMediatorService,
        VkKartaMediatorService,
        KontraktCommandService,
        AllSkogsvardTradslagService,
        VirkeskopskontraktLeverantorMediatorService,
        AtgardKedjaMediatorService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        VerticalCollapsableComponent,
        BlockComponent,
        VkKartaComponent,
        VirkeskopKontraktComponent,
        SkogsvardKontraktComponent,
        SkogsvardFpvKontraktComponent,
        BlockComponent,
        AsyncPipe
    ]
})
export class KontraktItemComponent implements OnInit, OnDestroy {

    @Output() get loading() {
        return this.kontraktMediatorService.loading$;
    }

    protected kontrakt: Kontrakt;
    protected readonly messages: RuleValidationMessage[] = [];

    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private _entity: IVirkeskopKontraktEntityDTO;

    get entity(): IVirkeskopKontraktEntityDTO {
        return this._entity;
    }

    @Input()
    set entity(entity: IVirkeskopKontraktEntityDTO) {
        this._entity = entity;
        this.initKontrakt();
    }

    @Input()
    set tabState(state: TabState) {
        this.kontraktMediatorService.tabStateHelper.tabState$.next(state);
    }

    protected get tabVisible$() {
        return this.kontraktMediatorService.tabStateHelper.effectivelyVisible$;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.initSubscriptions();
    }

    private clearValidations() {
        this.messages.forEach(m =>
            this.validationService.updateClientSideValidation(false, m));
        this.messages.splice(0);
    }

    private validate(tomDatum: Date): void {
        const kontrakt = this.kontraktMediatorService.kontrakt;
        const kontraktGuid = kontrakt.guid;

        this.clearValidations();

        // tre år framåt
        const today: Date = DateUtils.now();
        const todayFuture = new Date(today);
        todayFuture.setFullYear(today.getFullYear() + 3);

        const giltigtTill: Date = new Date(tomDatum);

        // Om giltigTill är efter idag + tre år --> ogiltigt datum!
        const inValidDate = moment(giltigtTill).isBefore(todayFuture, "day");
        const isGrot = kontrakt.isGrot;
        const nummer = kontrakt?.kontraktnummer ?? kontrakt.kontraktnummer;
        const benamning = kontrakt?.benamning;
        const msg = `${kontrakt.status} ${nummer} ${benamning} har en giltighetstid är kortare än tre år - vill du spara?`;

        if (inValidDate && isGrot) {
            this.messages.push({
                entityGuids: [kontraktGuid],
                message: msg,
                messageId: "kontraktGiltigtTomVarning",
                severity: RuleSeverityLevel.warning,
                strategy: RuleVisibilityStrategy.confirm,
                context: RuleErrorContext.save
            });
        }
    }

    private initSubscriptions() {
        this.kontraktMediatorService.kontrakt$
            .pipe(filter(k => k !== null),
                takeUntil(this.ngUnsubscribe$))
            .subscribe(k => {
                this.kontrakt = k;
                this.clearValidations();
            });

        this.kontraktMediatorService.kontraktGiltigtChanged$
            .pipe(filter(p => p !== null),
                takeUntil(this.ngUnsubscribe$))
            .subscribe(p => {
                this.callValidate(p.periodTom);
            });

        this.kontraktMediatorService.anbudGiltigtChanged$
            .pipe(filter(p => p !== null),
                takeUntil(this.ngUnsubscribe$))
            .subscribe(p => {
                this.callValidate(p.periodTom);
            });

        this.kontraktMediatorService.statusChanged$
            .pipe(filter(s => s !== null),
                takeUntil(this.ngUnsubscribe$))
            .subscribe(s => {
                const kontrakt = this.kontraktMediatorService.kontrakt;
                if (s === VirkeskopKontraktstatus.Kontrakt) {
                    this.callValidate(new Date(kontrakt.kontraktGiltigtTom));
                }
                if (s === VirkeskopKontraktstatus.Anbud) {
                    this.callValidate(new Date(kontrakt.anbudGiltighetTom));
                }
            });

        this.kontraktMediatorService.kontraktNettoarealTotal$.pipe(
            map(arealer => arealer.filter(this.getNettoarealCondition())),
            withLatestFrom(this.kontraktMediatorService.kontrakt$),
            filter(([
                _,
                kontrakt
            ]) => ObjectUtils.hasValue(kontrakt)),
            map(([
                arealer,
                kontrakt
            ]) => ({
                visible: arealer.reduce((a, b) => a + b.nettoareal, 0) > 0.5,
                kontraktGuid: kontrakt.guid,
                beteckningar: arealer.map(a => a.beteckning).join(" och ")
            })),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(result => this.validationService.updateClientSideValidation(result.visible, {
            entityGuids: [result.kontraktGuid],
            entityType: "kontrakt",
            message: `Den sammanlagda ytan av åtgärd ${result.beteckningar} överstiger 0,5 ha. Om de ska avverkas samtidigt krävs ` +
                "avverkningsanmälan på samtliga åtgärder. Överväg avverkningsanmälan till skogsstyrelsen",
            messageId: "allFAAtgardNettoArealAboveThreshold",
            severity: RuleSeverityLevel.warning,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save
        }));
    }

    private getNettoarealCondition() {
        const foryngringsavverkningOrVagLednAtgardstyp = [
            ...Atgardstyper.foryngringsavverkningstyper,
            Atgardstyper.VagLedning
        ];
        return (areal: IAtgardNettoareal) => foryngringsavverkningOrVagLednAtgardstyp.some(typ => typ === areal.atgardstyp)
            && !areal.avverkningsanmalanGodkantDatum
            && areal.nettoareal < 0.5;
    }

    private callValidate(tomDatum: Date): void {
        this.validate(tomDatum);
        this.messages
            .forEach(m => this.validationService.updateClientSideValidation(true, m));
    }

    private initKontrakt() {
        const entity = this.entity;
        this.kontraktMediatorService.initKontrakt(entity);
    }
}
